import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import { RequestStatus } from '../../../../enums/RequestStatusEnum';

function EditInvoiceModal({ showEditModal, handleModalClose, handleFormChange, editInvoiceData, setShowEditModal }) {
    const [vatValue, setVatValue] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [date, setDate] = useState("");
    const [referenceName, setReferenceName] = useState("");
    const [description, setDescription] = useState("");
    const [disabledButtons, setDisabledButtons] = useState({});
    const [isEditInvoiceCreated, setIsEditInvoiceCreated] = useState(false); // New state
    const [isEditCreditInvoiceAccept, setIsEditCreditInvoiceAccept] = useState(false);
    const [isRequestApproved, setIsRequestApproved] = useState(false);
    const { t } = useTranslation();
    useEffect(() => {
        if (editInvoiceData) {
            setVatValue(editInvoiceData.vat !== undefined ? editInvoiceData.vat : "");
            setDueDate(editInvoiceData.dueDate ? editInvoiceData.dueDate.split("T")[0] : "");
            setDate(editInvoiceData.createdDate ? editInvoiceData.createdDate.split("T")[0] : "");
            setReferenceName(editInvoiceData.customerAddress?.reference || "");
            setDescription(editInvoiceData.description || "");
        }
    }, [editInvoiceData]);

    useEffect(() => {
        const fetchInvoiceEditData = async () => {
            try {
                const response = await axios.get(`/InvoiceEdit/${editInvoiceData.invoiceNumber}?requestType=${RequestStatus.General}`);
                const data = response.data;

                setIsEditInvoiceCreated(data.isEditInvoiceCreated); // Set the new state
                setIsEditCreditInvoiceAccept(data.isAnyResponseOnEditCreditInvoice); // Set the new state
                setIsRequestApproved(data.isRequestApproved);
                if (data.invoiceEditItems.length === 0) {
                    setVatValue(editInvoiceData.vat !== undefined ? editInvoiceData.vat : "");
                    setDueDate(editInvoiceData.dueDate ? editInvoiceData.dueDate.split("T")[0] : "");
                    setReferenceName(editInvoiceData.customerAddress?.reference || "");
                    setDescription(editInvoiceData.description || "");
                } else {
                    setVatValue(data.invoiceEditItems.find(item => item.fieldName === "Vat")?.requestedValue || "");
                    setDueDate(data.invoiceEditItems.find(item => item.fieldName === "DueDate")?.requestedValue || "");
                    setReferenceName(data.invoiceEditItems.find(item => item.fieldName === "ReferenceName")?.requestedValue || "");
                    setDescription(data.invoiceEditItems.find(item => item.fieldName === "Description")?.requestedValue || "");
                }
            } catch (error) {
                toast.error(t("An error occurred while fetching invoice edit data. Please try again."));
            }
        };
    
        if (editInvoiceData && editInvoiceData.id) {
            fetchInvoiceEditData();
        }
    }, [editInvoiceData, t]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        handleFormChange(e);
        switch (name) {
            case "vat":
                setVatValue(value);
                break;
            case "dueDate":
                setDueDate(value);
                break;
            case "date":
                setDate(value);
                break;
            case "referenceName":
                setReferenceName(value);
                break;
            case "description":
                setDescription(value);
                break;
            default:
                break;
        }
    };

    const handleRequestButtonClick = async () => {
        try {
            const invoiceEditItems = [];
            if (vatValue !== editInvoiceData.vat) {
                invoiceEditItems.push({ fieldName: "Vat", requestedValue: vatValue });
            }
            if (dueDate !== (editInvoiceData.dueDate ? editInvoiceData.dueDate.split("T")[0] : "")) {
                invoiceEditItems.push({ fieldName: "DueDate", requestedValue: dueDate });
            }
            if (referenceName !== (editInvoiceData.customerAddress?.reference || "")) {
                invoiceEditItems.push({ fieldName: "ReferenceName", requestedValue: referenceName });
            }
            if (description !== (editInvoiceData.description || "")) {
                invoiceEditItems.push({ fieldName: "Description", requestedValue: description });
            }

            if (invoiceEditItems.length === 0) {
                toast.info(t("No changes detected to request edits."));
                return;
            }

            const requestBody = {
                invoiceId: editInvoiceData.id,
                invoiceEditItems,
            };
            await axios.post("/InvoiceEdit", requestBody);
            toast.success(t("Your request for invoice") + " " + `#${editInvoiceData.invoiceNumber}` + " " + t("is in progress. You will receive a confirmation once they are approved or declined."));
            setShowEditModal(false);
            setDisabledButtons(prevState => ({ ...prevState, [editInvoiceData.id]: true }));
        } catch (error) {
            toast.error(t("An error occurred while requesting edits. Please try again."));
        }
    };

    return (
        <>
            <Modal show={showEditModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Edit Invoice")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col>
                                <Form.Group controlId="formVAT">
                                    <Form.Label>{t("VAT")}</Form.Label>
                                    <Form.Control as="select" name="vat" value={vatValue} onChange={handleInputChange}>
                                        <option value="">{t("Select VAT")}</option>
                                        <option value="0">0%</option>
                                        <option value="6">6%</option>
                                        <option value="12">12%</option>
                                        <option value="25">25%</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group controlId="formDueDate">
                                    <Form.Label>{t("Due Date")}</Form.Label>
                                    <Form.Control type="date" name="dueDate" value={dueDate} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="my-2">
                            <Col>
                                <Form.Group controlId="formReferenceName">
                                    <Form.Label>{t("Reference Name")}</Form.Label>
                                    <Form.Control type="text" name="referenceName" value={referenceName} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group controlId="formDescription" className="my-3">
                            <Form.Label>{t("Description")}</Form.Label>
                            <Form.Control as="textarea" rows={3} name="description" value={description} onChange={handleInputChange} style={{ margin: "0px 0px 10px 0px" }} placeholder="Describe your work in as much detail as you can. You can also add if you have mileage allowance and/or allowance in this box. This information constitutes the description on the invoice sent to your customer." />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        {isEditInvoiceCreated ? t("Close") : t("Cancel")}
                    </Button>
                    {isEditCreditInvoiceAccept ? (
                       <Button variant={isRequestApproved ? "success" : "danger"} disabled>
                       {t(isRequestApproved ? "Approved" : "Declined")}
                   </Button>
                    ) : isEditInvoiceCreated ? (
                        <Button variant="primary" disabled>
                            {t("Already Requested")}
                        </Button>
                    ) : (
                        <Button variant="primary" onClick={handleRequestButtonClick} disabled={disabledButtons[editInvoiceData.id]}>
                            {t("Request Edits")}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default EditInvoiceModal;